import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getExercise } from '../api/exercises';
import { getMediaFileUrl } from '../api/media';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import DifficultyStars from '../components/common/DifficultyStars';
import TagBadge from '../components/common/TagBadge';

const API_BASE = process.env.REACT_APP_API_URL || '';

function getEmbedUrl(media) {
  const info = media?.meta_info;
  if (!info) return null;
  if (info.platform === 'youtube') return `https://www.youtube.com/embed/${info.video_id}`;
  if (info.platform === 'vimeo') return `https://player.vimeo.com/video/${info.video_id}`;
  return null;
}

export default function ExerciseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['admin', 'trainer', 'super_admin'].includes(user?.role);

  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getExercise(id)
      .then(setExercise)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (error) return <AlertBanner message={error} type="danger" />;
  if (!exercise) return null;

  const mediaList = exercise.media || [];
  const images = mediaList.filter((m) => (m.media?.type || m.type) === 'image');
  const explVideos = mediaList.filter((m) => m.media_type === 'video');
  const presVideo = mediaList.find((m) => m.media_type === 'presenter_video');

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Header */}
      <div className="d-flex align-items-start justify-content-between mb-4 gap-3">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-1" />Zurück
          </button>
          <h3 className="fw-bold mb-1">{exercise.name}</h3>
          <div className="d-flex align-items-center gap-3 flex-wrap text-muted small">
            <DifficultyStars value={exercise.difficulty} />
            {exercise.equipment_needed && (
              <span><i className="bi bi-tools me-1" />{exercise.equipment_needed}</span>
            )}
            <span>{exercise.tags?.map((t) => <TagBadge key={t.id} tag={t} />)}</span>
          </div>
        </div>
        {canEdit && (
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(`/exercises/${id}/edit`)}
          >
            <i className="bi bi-pencil me-1" />Bearbeiten
          </button>
        )}
      </div>

      {/* Description */}
      {exercise.description && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-subtitle mb-2 text-muted fw-semibold">Beschreibung</h6>
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{exercise.description}</p>
          </div>
        </div>
      )}

      {/* Instructions */}
      {exercise.instructions && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-subtitle mb-2 text-muted fw-semibold">Anleitung</h6>
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{exercise.instructions}</p>
          </div>
        </div>
      )}

      {/* Images */}
      {images.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-subtitle mb-3 text-muted fw-semibold">
              <i className="bi bi-image me-1" />Bilder
            </h6>
            <div className="d-flex flex-wrap gap-3">
              {images.map((rel) => {
                const mid = rel.media?.id || rel.media_id;
                return (
                  <img
                    key={mid}
                    src={getMediaFileUrl(mid)}
                    alt={exercise.name}
                    style={{ height: 160, maxWidth: '100%', objectFit: 'cover', borderRadius: 8 }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Explanation videos */}
      {explVideos.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-subtitle mb-3 text-muted fw-semibold">
              <i className="bi bi-camera-video me-1" />Erklärungsvideos
            </h6>
            <div className="d-flex flex-wrap gap-3">
              {explVideos.map((rel) => {
                const mediaObj = rel.media || rel;
                const embedUrl = getEmbedUrl(mediaObj);
                return embedUrl ? (
                  <iframe
                    key={rel.id}
                    src={embedUrl}
                    title="Erklärungsvideo"
                    style={{ width: '100%', maxWidth: 320, aspectRatio: '16/9', border: 'none', borderRadius: 8 }}
                    allowFullScreen
                  />
                ) : null;
              })}
            </div>
          </div>
        </div>
      )}

      {/* Presenter video */}
      {presVideo && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-subtitle mb-3 text-muted fw-semibold">
              <i className="bi bi-play-circle me-1 text-danger" />Ablaufvideo
              <span className="ms-2 badge bg-danger" style={{ fontSize: '0.7rem' }}>Präsentation</span>
            </h6>
            {(() => {
              const embedUrl = getEmbedUrl(presVideo.media || presVideo);
              return embedUrl ? (
                <iframe
                  src={embedUrl}
                  title="Ablaufvideo"
                  style={{ width: '100%', maxWidth: 560, aspectRatio: '16/9', border: 'none', borderRadius: 8 }}
                  allowFullScreen
                />
              ) : null;
            })()}
          </div>
        </div>
      )}

      {images.length === 0 && explVideos.length === 0 && !presVideo && !exercise.description && !exercise.instructions && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-activity fs-1 d-block mb-2 opacity-25" />
          Noch keine weiteren Details hinterlegt.
        </div>
      )}
    </div>
  );
}
